import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useCustomEvent } from '@beef/shop-common';
import { Device, PromoInstallment } from '@beef/smart-kit';
import { Tooltip } from '@beef/ui-kit';
import PropTypes from 'prop-types';

import K50BotInvisibleSeoTag from 'components/K50BotInvisibleSeoTag';
import { COOKIES_NAMES } from 'constants/cookies';
import pushEvent, {
  ADD_TO_COMPARISON_FROM_CATALOG,
  ADD_TO_FAVORITE_FROM_CATALOG,
  EVENT_CATALOG_PRODUCTS_SEEN,
  REMOVE_FROM_COMPARISON_FROM_CATALOG,
  REMOVE_FROM_FAVORITE_FROM_CATALOG,
  SHOW_DEVICE_DETAILS_WITH_TRADE_IN,
} from 'pushEvent';
import { cookiesDataService } from 'services/cookiesDataService';
import { toggleCompareProduct } from 'store/common/compare/actions';
import { getCompareUrl } from 'store/common/compare/selectors';
import { getIsFavoritesActive, getisCompareActive } from 'store/common/settings/selectors';
import { onOpenDeviceCatalogModal } from 'store/ProductCatalog/actions';
import * as CatalogProducts from 'store/ProductCatalog/products/selectors';
import { getRealMainCollectionName } from 'store/ProductCatalog/products/selectors';
import { getCatalogSubCollectionName } from 'store/ProductCatalog/selectors';
import { isTradeInActive } from 'store/widgets/TradeInCalculator/selectors';

import { useCatalogDevicePurchase } from './useCatalogDevicePurchase';

const getMappedBadges = (badges) =>
  badges.reduce((acc, cur) => {
    let badge = {
      children: cur.title,
      help: cur.content,
    };

    if (cur.variant) {
      badge = {
        ...badge,
        variant: cur.variant,
      };
    }

    if (cur.textColor) {
      badge = {
        ...badge,
        textColor: cur.textColor,
      };
    }

    acc.push(badge);
    return acc;
  }, []);

// https://servicedesk.veon.com/browse/ES-554 корректное отображение цены
const getPriceData = (device) => {
  const isBundleState = !!device?.promotionBundle?.state;
  const isPromo = device?.promotionBundle?.isOnlyWithPromo;
  const isValuePreposition = device?.promotionBundle?.valuePreposition;
  const isBundlePrice = isBundleState && !isValuePreposition && isPromo;

  let price = device?.price?.value;
  const oldPrice = device?.price?.oldValue;

  if (isValuePreposition || isBundlePrice) {
    price = device?.promotionBundle?.bundlePrice;
  }

  return {
    price,
    oldPrice: oldPrice === price ? undefined : oldPrice,
  };
};

const getPromoInstallments = (installments) => {
  if (!installments || !Array.isArray(installments)) {
    return undefined;
  }

  return installments.map(({ count, price, text, tooltip, type }) => {
    const promoInstallmentComp = (
      <PromoInstallment
        count={count}
        key={`${type}-${price}-${text}-${count}`}
        price={Number(price)}
        text={text}
        type={type}
      />
    );
    return tooltip ?
        <Tooltip message={tooltip} position="bottom">
          {promoInstallmentComp}
        </Tooltip>
      : promoInstallmentComp;
  });
};

const DeviceCardLayout = ({
  device,
  onToggleCompare,
  idsInCmp,
  isTradeIn,
  onViewed,
  onOpenDeviceModal,
}) => {
  const dispatch = useDispatch();
  const [isFavoriteSelected, setIsFavoriteSelected] = useState(false);
  const { publishDOM } = useCustomEvent();
  const { title, onClick, variant } = useCatalogDevicePurchase(device);
  const collectionName = useSelector(getRealMainCollectionName)[device.id];
  const subCollectionName = useSelector(getCatalogSubCollectionName);

  const [deviceRef] = useInView({
    triggerOnce: true,
    threshold: 1,
    onChange: (inView) => {
      if (inView) {
        pushEvent(EVENT_CATALOG_PRODUCTS_SEEN, { collectionName, subCollectionName, ...device });
        onViewed(device);
      }
    },
  });

  const onOpenModal = useCallback(() => {
    if (isTradeIn) {
      pushEvent(SHOW_DEVICE_DETAILS_WITH_TRADE_IN, {
        id: device?.article,
        name: device?.title,
        url: device?.url,
      });
    }

    onOpenDeviceModal(device);
  }, [device?.article, device?.index, device?.title, device?.url, dispatch, isTradeIn]);

  const promoBadges = useMemo(
    () => (device?.badges && device.badges?.length ? getMappedBadges(device?.badges) : undefined),
    [device],
  );

  const purchaseData = {
    title,
    onClick,
    variant,
  };

  const { price, oldPrice } = getPriceData(device);

  function isCompared(deviceId) {
    return idsInCmp[deviceId];
  }

  function onCmpClick() {
    if (isCompared(device.id)) {
      pushEvent(REMOVE_FROM_COMPARISON_FROM_CATALOG, {
        id: device?.article,
        name: device?.title,
        brand: device?.brandName,
        position: device?.index,
        place: 'catalog',
      });
    } else {
      pushEvent(ADD_TO_COMPARISON_FROM_CATALOG, {
        id: device?.article,
        name: device?.title,
        brand: device?.brandName,
        position: device?.index,
        place: 'catalog',
      });
    }
    onToggleCompare({ productId: device.id });
  }

  const isFavoritesActive = useSelector(getIsFavoritesActive);
  const isCompareActive = useSelector(getisCompareActive);

  const deviceData = {
    id: device.id,
    highlight: device?.highlight || null,
    imgOptions: {
      src: device?.thumbUrl,
    },
    name: device?.title,
    price,
    oldPrice,
    link: device?.url,
    promoBadges,
    slug: device?.urlSlug,
    isInCmp: isCompared(device.id),
    cmpTooltipMsg: 'Добавить в сравнение',
    onCmpClick: isCompareActive ? () => onCmpClick() : null,
    cmpTooltipMsgPos: 'bottom',
    promoInstallments: getPromoInstallments(device?.installments),
  };

  const setIsFavorite = useCallback(() => {
    const pushName =
      device?.isFavorite ? REMOVE_FROM_FAVORITE_FROM_CATALOG : ADD_TO_FAVORITE_FROM_CATALOG;
    pushEvent(pushName, {
      id: device?.article,
      name: device?.title,
      brand: device?.brandName,
      position: device?.index,
      place: 'catalog',
    });
    const favoriteDeviceList = cookiesDataService.setActualData(
      COOKIES_NAMES.FAVORITE_DEVICES,
      device.id,
    );
    publishDOM(COOKIES_NAMES.FAVORITE_DEVICES, favoriteDeviceList);
  }, [dispatch, device]);
  /* TODO не добавляются стили из @beef/smart-kit после перезагрузки страницы (возможно из-за SSR) */
  useEffect(() => {
    setIsFavoriteSelected(!!device?.isFavorite);
  }, [device?.isFavorite]);

  return (
    <div data-identity={device?.id} ref={deviceRef}>
      <Device
        device={deviceData}
        favoriteTooltip="Добавить в избранное"
        isFavoriteSelected={isFavoriteSelected}
        isWithModal
        onOpenModal={onOpenModal}
        purchaseData={purchaseData}
        setFavorite={isFavoritesActive && setIsFavorite}
      />
      <K50BotInvisibleSeoTag isOutOfStock={device?.isOutOfStock} />
    </div>
  );
};

DeviceCardLayout.propTypes = {
  onToggleCompare: PropTypes.func,
  idsInCmp: PropTypes.object,
  device: PropTypes.any,
};

const mapStateToProps = (state) => ({
  compareUrl: getCompareUrl(state),
  id: CatalogProducts.getProductIdsDict(state),
  idsInCmp: CatalogProducts.getIsProductComparedDict(state),
  isTradeIn: isTradeInActive(state),
});

const mapDispatchToProps = {
  onToggleCompare: (id) => toggleCompareProduct(id),
  onOpenDeviceModal: onOpenDeviceCatalogModal,
};

export const DeviceCard = connect(mapStateToProps, mapDispatchToProps)(DeviceCardLayout);
